import * as React from 'react';
import { Button } from 'reactstrap';
import { useEffect, useState } from 'react';
import { Communicator } from '../../tools/Communication';
import i18n from '@/utils/i18n';
import { LayoutStoreInstance } from '../../LayoutStore';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { isNullOrWhitespace } from '@/utils/stringHelper';
import { EditItem } from '../../components/Layout';
import { DEFAULT_CONTAINER_TYPE_ICON } from '@/utils/imageUrl';
import { ContainerCollection } from './model';
import { useHistory } from 'react-router';
import { ContentSection } from '../../components/Layout/ContentSection';

export default function ContainerCollectionByLocationView() {
  const urlParams = new URLSearchParams(window.location.search);
  const defaultLocation = urlParams.get('location') || '-';
  const history = useHistory();
  const [selectedLocation, setSelectedLocation] = useState(defaultLocation);
  const [newLocation, setNewLocation] = useState('');
  const [locations, setLocations] = useState<string[]>([]);
  const [containerCollections, setContainerCollections] = useState<
    ContainerCollection[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const promises = [
      Communicator.Get('api/3/ContainerCollection/Locations', {})
        .then(async (response) => {
          const locations = await response.json();
          setLocations(locations);
          updateSelectedLocation(defaultLocation, locations);
        })
        .catch((error) => {
          LayoutStoreInstance.setError('Failed to load locations: ' + error);
        }),
    ];
    Promise.all(promises).then(() => setIsLoading(false));
  }, []);

  async function updateSelectedLocation(location: string, locs = locations) {
    console.log({ location, locs });
    if (!isNullOrWhitespace(location) && locs.includes(location)) {
      setIsLoading(true);
      setSelectedLocation(location);
      Communicator.Get('api/3/ContainerCollection/GetByLocation', { location })
        .then(async (response) => {
          const collections = await response.json();
          setContainerCollections(collections);
        })
        .catch((error) => {
          LayoutStoreInstance.setError('Failed to load collections: ' + error);
        });
      history.push({
        search: `?location=${location}`,
      });
      setIsLoading(false);
    } else {
      setSelectedLocation('-');
    }
  }

  async function addLocation() {
    if (locations.includes(newLocation)) {
      alert('ContainerCollections:locationAlreadyPresent');
    } else {
      setIsLoading(true);
      locations.push(newLocation);
      Communicator.PostData('Config/SetConfigValue', {
        Key: 'ContainerCollectionLocations',
        Value: JSON.stringify(locations),
      })
        .then(async (response) => {
          const locations = await response.json();
          setLocations(locations);
        })
        .catch((error) => {
          LayoutStoreInstance.setError('Failed to save locations: ' + error);
        });
      setIsLoading(false);
    }
  }

  return (
    <ContentSection
      title={i18n.t('Common:containerCollection')}
      collapsable={false}
    >
      <EditItem title={i18n.t('ContainerCollection:searchByLocations')}>
        <div className="input-group mb-3">
          <span className="input-group-text" id="basic-addon1">
            {i18n.t('ContainerCollection:location')}
          </span>
          <select
            className="form-control custom-select"
            onChange={(e) => updateSelectedLocation(e.target.value)}
            value={selectedLocation}
          >
            {locations.concat(['-']).map((l) => (
              <option>{l}</option>
            ))}
          </select>
        </div>
        <DataTable
          value={containerCollections}
          dataKey="id"
          responsiveLayout="scroll"
          loading={isLoading}
          className="p-datatable-striped"
          style={{ width: '100%', overflowX: 'scroll' }}
        >
          <Column
            body={(c: ContainerCollection) =>
              renderCell(
                `/container-collection/${c.id}`,
                true,
                i18n.t('Common:view'),
              )
            }
            header={i18n.t('Common:view')}
          />
          <Column
            headerStyle={{ width: '100' }}
            body={(c: ContainerCollection) => renderCell(c.count)}
            header={i18n.t('ContainerCollection:count')}
          />
          <Column
            headerStyle={{ width: '100' }}
            body={(cc: ContainerCollection) =>
              cc.containers.map((c, index) => (
                <div style={{ float: 'left' }}>
                  {' '}
                  <a href={`/container/${c.id}`}>
                    <span>{c.name || '-'}</span>
                  </a>
                  {index != cc.containers.length - 1 && <span>,</span>}
                </div>
              ))
            }
            header={i18n.t('ContainerCollection:containerNames')}
          />
          <Column
            headerStyle={{ width: '100' }}
            body={(cc: ContainerCollection) => getContainerTypeImages(cc)}
            header={i18n.t('ContainerCollection:containerTypes')}
          />
        </DataTable>
      </EditItem>
      <EditItem title={i18n.t('ContainerCollection:addNewLocation')}>
        <div className="input-group mb-3">
          <span className="input-group-text" id="basic-addon1">
            {i18n.t('Common:name')}
          </span>
          <input
            className="form-control"
            value={newLocation}
            onChange={(e) => setNewLocation(e.target.value)}
          />
        </div>
        <Button color="primary" size="sm" onClick={addLocation}>
          {isLoading ? i18n.t('Common:adding') : i18n.t('Common:add')}
        </Button>
      </EditItem>
    </ContentSection>
  );
}

function getContainerTypeImages(collection: ContainerCollection) {
  const containerTypesByTypeId = Object.assign(
    {},
    ...collection.containers.map((c) => ({
      [c.typeId]: {
        imageUrl: c.typeImageUrl,
        iconUrl: c.typeIconUrl,
        name: c.typeName,
      },
    })),
  );
  return Object.keys(containerTypesByTypeId).map((id) => (
    <img
      style={{ float: 'left', width: '3.2rem', height: '3rem' }}
      src={
        containerTypesByTypeId[id].imageUrl ||
        containerTypesByTypeId[id].iconUrl ||
        DEFAULT_CONTAINER_TYPE_ICON
      }
      alt={containerTypesByTypeId[id].name || '-'}
    />
  ));
}

function renderCell(content: any, displayAsAnchor = false, alias = '') {
  if (displayAsAnchor) {
    return (
      <a href={content}>
        <span>{alias}</span>
      </a>
    );
  }
  return (
    <div>
      <span>{content}</span>
    </div>
  );
}
